import React, { Component } from 'react';
import Form from 'react-bootstrap/Form';
import CourseCard from '../CourseCard';
import { URL } from '../BackendApi';
import './style.scss';

const MONTH_NAMES_FI = [
  'Tammikuu', 'Helmikuu', 'Maaliskuu', 'Huhtikuu', 'Toukokuu', 'Kes\u00e4kuu',
  'Hein\u00e4kuu', 'Elokuu', 'Syyskuu', 'Lokakuu', 'Marraskuu', 'Joulukuu'
];

class CourseCalendar extends Component {
  constructor(props) {
    super(props);

    this.state = {
      courses: [],
      categories: [],
      loading: true,
      error: null,
      selectedCategory: props.filterCategory || '',
      selectedCity: '',
      mobileShowAll: false
    };

    this.handleCategoryChange = this.handleCategoryChange.bind(this);
    this.handleCityChange = this.handleCityChange.bind(this);
  }

  componentDidMount() {
    this.fetchCourses();
  }

  fetchCourses() {
    this.setState({ loading: true });

    const params = new URLSearchParams();
    if (this.props.filterCategory) params.append('category', this.props.filterCategory);
    if (this.props.filterCity) params.append('city', this.props.filterCity);
    if (this.props.filterName) params.append('name', this.props.filterName);

    const queryString = params.toString();
    const fetchUrl = `${URL}/courses/public${queryString ? '?' + queryString : ''}`;

    fetch(fetchUrl)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then(data => {
        this.setState({ courses: data.courses, categories: data.categories, loading: false, error: null });
      })
      .catch(error => {
        console.error('Error fetching courses:', error);
        this.setState({ error: 'Koulutuksia ei voitu ladata.', loading: false });
      });
  }

  handleCategoryChange(e) {
    this.setState({ selectedCategory: e.target.value });
  }

  handleCityChange(e) {
    this.setState({ selectedCity: e.target.value });
  }

  getFilteredCourses() {
    const { courses, selectedCategory, selectedCity } = this.state;
    let filtered = courses;

    if (selectedCategory) {
      filtered = filtered.filter(c => c.category === selectedCategory);
    }
    if (selectedCity) {
      filtered = filtered.filter(c => c.city === selectedCity);
    }

    return filtered;
  }

  groupByMonth(courses) {
    const groups = {};
    courses.forEach(course => {
      const date = new Date(course.course_date_iso);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const label = `${MONTH_NAMES_FI[date.getMonth()]} ${date.getFullYear()}`;
      if (!groups[key]) {
        groups[key] = { label, courses: [] };
      }
      groups[key].courses.push(course);
    });
    return Object.values(groups);
  }

  getCities() {
    const cities = [...new Set(this.state.courses.map(c => c.city))];
    cities.sort();
    return cities;
  }

  limitCourses(grouped, limit) {
    const result = [];
    let count = 0;
    for (const group of grouped) {
      if (count >= limit) break;
      const remaining = limit - count;
      if (group.courses.length <= remaining) {
        result.push(group);
        count += group.courses.length;
      } else {
        result.push({ label: group.label, courses: group.courses.slice(0, remaining) });
        count += remaining;
      }
    }
    return result;
  }

  render() {
    const { loading, error, mobileShowAll } = this.state;
    const filtered = this.getFilteredCourses();
    const grouped = this.groupByMonth(filtered);
    const cities = this.getCities();
    const totalCourses = filtered.length;

    const maxHeight = this.props.maxHeight || 700;
    const mobileLimit = this.props.mobileLimit || 6;

    const showCategoryFilter = !this.props.filterCategory;
    const showCityFilter = !this.props.filterCity;

    const mobileGrouped = (!mobileShowAll && totalCourses > mobileLimit)
      ? this.limitCourses(grouped, mobileLimit)
      : grouped;

    return (
      <div className="course-calendar">
        <h3 className="course-calendar__title">Tulevat koulutukset:</h3>

        {(showCategoryFilter || showCityFilter) && (
          <div className="course-calendar__filters">
            <span className="course-calendar__filters-label">Hae koulutuksia:</span>
            {showCategoryFilter && (
              <Form.Control
                as="select"
                value={this.state.selectedCategory}
                onChange={this.handleCategoryChange}
                className="course-calendar__filter"
              >
                <option value="">Koulutus</option>
                {this.state.categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Form.Control>
            )}
            {showCityFilter && (
              <Form.Control
                as="select"
                value={this.state.selectedCity}
                onChange={this.handleCityChange}
                className="course-calendar__filter"
              >
                <option value="">Paikkakunta</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </Form.Control>
            )}
          </div>
        )}

        {loading && <p className="course-calendar__loading">Ladataan koulutuksia...</p>}
        {error && <p className="course-calendar__error">{error}</p>}

        {!loading && !error && filtered.length === 0 && (
          <p className="course-calendar__empty">Ei tulevia koulutuksia.</p>
        )}

        {/* Desktop: scrollable container */}
        <div className="course-calendar__scroll" style={{ maxHeight: maxHeight + 'px' }}>
          {grouped.map(group => (
            <div key={group.label} className="course-calendar__month">
              <h4 className="course-calendar__month-title">{group.label}</h4>
              {group.courses.map(course => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          ))}
        </div>

        {/* Mobile: limited with show more */}
        <div className="course-calendar__mobile">
          {mobileGrouped.map(group => (
            <div key={group.label} className="course-calendar__month">
              <h4 className="course-calendar__month-title">{group.label}</h4>
              {group.courses.map(course => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          ))}
          {!mobileShowAll && totalCourses > mobileLimit && (
            <button
              className="course-calendar__show-more"
              onClick={() => this.setState({ mobileShowAll: true })}
            >
              Näytä lisää ({totalCourses - mobileLimit} koulutusta)
            </button>
          )}
        </div>
      </div>
    );
  }
}

export default CourseCalendar;
